import React, {useEffect, useState} from 'react';
import {
	getAiCreditRequestProgress,
	getAiCreditUsage,
	isAiCreditsExhausted,
	subscribeAiCreditRequestSettled,
	useAiCreditUsage,
} from '../../../../components/gen-ai/aiCreditUsage';
import {notifyAiCreditsExhausted} from '../../../../components/gen-ai/aiCreditsSignal';
import {isNovaInvocationEnabled} from './novaInvocationGate';

export type NovaCreditsBlock = {blocked: boolean; resetsOn: string | null};

// The request number the usage store had started when the server last refused a run for a spent
// allowance. The refusal is the server's own answer, so it blocks until a request asked for after
// it settles. From then on the fetched balance decides again, and a failed or unknown one never
// blocks. A request already on its way when the refusal landed may predate it, so it does not count.
let refusalAfterRequest: number | null = null;
const refusalListeners = new Set<() => void>();
let stopWatchingSettles: (() => void) | null = null;

const refusalHolds = (): boolean => refusalAfterRequest !== null && getAiCreditRequestProgress().settled <= refusalAfterRequest;

// A settle that releases the hold may bring no new balance (a failure, or unknown when nothing was
// shown), which re-renders nothing on its own, so the release is announced to every control.
const onRequestSettled = (): void => {
	if (refusalHolds()) return;
	refusalAfterRequest = null;
	stopWatchingSettles?.();
	stopWatchingSettles = null;
	refusalListeners.forEach(listener => listener());
};

/**
 * Whether a Nova run may not start because the AI allowance is spent, read outside React. An
 * unknown balance never blocks: the server still refuses a run it will not pay for.
 */
export const isNovaCreditsBlocked = (): boolean => refusalHolds() || isAiCreditsExhausted(getAiCreditUsage());

/** Called when the server refused a run for a spent allowance. */
export const noteNovaCreditsRefused = (): void => {
	refusalAfterRequest = getAiCreditRequestProgress().started;
	if (stopWatchingSettles === null) stopWatchingSettles = subscribeAiCreditRequestSettled(onRequestSettled);
	refusalListeners.forEach(listener => listener());
	notifyAiCreditsExhausted();
};

/** A point to measure from: the balance requests started so far. */
export const creditsRequestMark = (): number => getAiCreditRequestProgress().started;

/**
 * Whether a balance asked for after `mark` showed credits to spend. Measured from a refusal, it says
 * the allowance has come back since, whatever has happened to it again afterwards.
 */
export const creditsAvailableSince = (mark: number): boolean => getAiCreditRequestProgress().available > mark;

/** The spent-allowance block for every Nova control, and the date it lifts. */
export const useNovaCreditsBlock = (enabled = true): NovaCreditsBlock => {
	const usage = useAiCreditUsage(enabled);
	const [, setRefusalCount] = useState(0);

	useEffect(() => {
		if (!enabled) return undefined;
		const listener = (): void => setRefusalCount(count => count + 1);
		refusalListeners.add(listener);
		return () => {
			refusalListeners.delete(listener);
		};
	}, [enabled]);

	if (!enabled) return {blocked: false, resetsOn: null};
	return {blocked: refusalHolds() || isAiCreditsExhausted(usage), resetsOn: usage?.periodEnd ?? null};
};

export type WithNovaCreditsBlockProps = {novaCreditsBlock: NovaCreditsBlock};

/** Hands useNovaCreditsBlock's answer to a class component as the `novaCreditsBlock` prop. */
export function withNovaCreditsBlock<P extends WithNovaCreditsBlockProps>(
	Wrapped: React.ComponentType<P>
): React.FC<Omit<P, 'novaCreditsBlock'>> {
	const WithNovaCreditsBlock: React.FC<Omit<P, 'novaCreditsBlock'>> = props => {
		const novaCreditsBlock = useNovaCreditsBlock(isNovaInvocationEnabled());
		return React.createElement(Wrapped, {...(props as P), novaCreditsBlock});
	};
	return WithNovaCreditsBlock;
}
